/**
 * Algoritmo de aprendizaje empírico de patrones de alimentación.
 *
 * Conceptos:
 *  - "Ciclo": período entre refill y confirmEmpty. Captura cuántas feedings
 *    salen de una recarga.
 *  - EMA (Exponential Moving Average): promedio que pondera más los valores
 *    recientes. Permite adaptarse a cambios reales (cachorro creciendo,
 *    cambio de marca de comida).
 *  - Outliers: ciclos que se alejan >2σ del promedio se excluyen del EMA
 *    para que un evento anómalo (derrame, error de carga) no contamine.
 *
 * Todas las funciones son puras: sin side effects, sin acceso a Firestore.
 * Los tests de convergencia simulan cientos de ciclos en milisegundos.
 */

import { LEARNING } from '../config/constants';

// =====================================================================
// EMA — Exponential Moving Average
// =====================================================================

/**
 * Actualiza un EMA con un nuevo valor.
 *
 * Fórmula clásica: EMA_n = α * x_n + (1 - α) * EMA_{n-1}
 *
 * @param {number|null} currentEma - EMA actual, null si es el primer valor
 * @param {number} newValue - Valor a incorporar
 * @param {number} alpha - Peso del valor nuevo, default LEARNING.EMA_ALPHA
 * @returns {number}
 */
export const updateEMA = (currentEma, newValue, alpha = LEARNING.EMA_ALPHA) => {
  if (currentEma === null || currentEma === undefined) return newValue;
  return alpha * newValue + (1 - alpha) * currentEma;
};

/**
 * Calcula la desviación estándar de una muestra.
 * Usamos Bessel's correction (dividir por n-1) por ser muestra, no población.
 */
export const stdDev = (values) => {
  if (!values || values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance =
    values.reduce((s, v) => s + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
};

/**
 * Calcula la media aritmética simple.
 */
export const mean = (values) => {
  if (!values || values.length === 0) return 0;
  return values.reduce((s, v) => s + v, 0) / values.length;
};

// =====================================================================
// DETECCIÓN DE OUTLIERS
// =====================================================================

/**
 * Determina si un nuevo valor es outlier respecto al historial.
 *
 * Reglas:
 *  - Si hay menos de MIN_CYCLES_FOR_OUTLIERS ciclos previos, NUNCA es outlier
 *    (no hay σ confiable).
 *  - Si |value - mean| > sigma_threshold * σ, es outlier.
 *
 * @param {number} value - Valor candidato
 * @param {number[]} history - Valores previos (no incluye `value`)
 * @returns {{ isOutlier: boolean, reason?: string }}
 */
export const detectOutlier = (value, history) => {
  if (!history || history.length < LEARNING.MIN_CYCLES_FOR_OUTLIERS) {
    return { isOutlier: false };
  }

  const m = mean(history);
  const sd = stdDev(history);

  // Si no hay varianza en el historial, no tenemos suficiente información
  // estadística para definir qué es outlier. Aceptamos el valor.
  // En producción esto es raro (siempre hay algo de ruido), pero permite
  // que el algoritmo se adapte a shifts reales (cachorro creciendo,
  // cambio de marca) cuando el historial sintético es perfectamente uniforme.
  if (sd === 0) {
    return { isOutlier: false };
  }

  const zScore = Math.abs(value - m) / sd;
  if (zScore > LEARNING.OUTLIER_SIGMA_THRESHOLD) {
    return {
      isOutlier: true,
      reason: `z_score_${zScore.toFixed(2)}`,
    };
  }
  return { isOutlier: false };
};

// =====================================================================
// CICLO: creación y evaluación
// =====================================================================

/**
 * Crea un ciclo nuevo con los campos iniciales.
 * El cycleId lo decide el caller — generalmente un UUID o un timestamp.
 */
export const createCycle = ({ cycleId, startedAt, refillAmountGr = 0 }) => ({
  cycleId,
  startedAt,
  endedAt: null,
  feedingsCount: 0,
  totalAmountFedGr: 0,
  refillAmountGr,
  refillsCount: refillAmountGr > 0 ? 1 : 0,
  isOutlier: false,
  outlierReason: null,
});

/**
 * Suma una feeding al ciclo activo.
 * Inmutable: retorna un objeto nuevo.
 */
export const addFeedingToCycle = (cycle, amountGr) => ({
  ...cycle,
  feedingsCount: cycle.feedingsCount + 1,
  totalAmountFedGr: cycle.totalAmountFedGr + amountGr,
});

/**
 * Acumula un refill parcial en el ciclo activo.
 * Caso real: usuario rellena de a poquito durante la semana.
 */
export const addRefillToCycle = (cycle, amountGr) => ({
  ...cycle,
  refillAmountGr: cycle.refillAmountGr + amountGr,
  refillsCount: cycle.refillsCount + 1,
});

/**
 * Cierra un ciclo con timestamp de confirmEmpty.
 */
export const closeCycle = (cycle, endedAt) => ({
  ...cycle,
  endedAt,
});

// =====================================================================
// ACTUALIZACIÓN DE PROMEDIOS
// =====================================================================

/**
 * Toma un ciclo recién cerrado + el estado de aprendizaje actual,
 * y retorna el nuevo estado de aprendizaje.
 *
 * Pasos:
 *  1. Determinar si el ciclo es outlier (vs feedingsCount histórico).
 *  2. Si no es outlier: actualizar EMA de feedingsPerRefill.
 *  3. Si refillsCount > 0: incorporar al EMA de refillsPerBag (aún sin lógica
 *     de "bolsa" completa — eso requiere otro tipo de ciclo).
 *  4. Actualizar contadores.
 *  5. Determinar learningStatus.
 *
 * @param {object} closedCycle - El ciclo recién cerrado
 * @param {object} state - Estado anterior del aprendizaje
 * @param {Array} state.cycles - Ciclos previos (sin el recién cerrado)
 * @param {number|null} state.avgFeedingsPerRefill - EMA actual
 * @param {number|null} state.avgRefillsPerBag - EMA actual
 * @param {number} state.cyclesCompleted - Contador acumulado
 */
export const incorporateClosedCycle = (closedCycle, state) => {
  const validHistory = state.cycles
    .filter((c) => !c.isOutlier && c.endedAt)
    .map((c) => c.feedingsCount);

  const outlierResult = detectOutlier(closedCycle.feedingsCount, validHistory);
  const cycleWithOutlierFlag = {
    ...closedCycle,
    isOutlier: outlierResult.isOutlier,
    outlierReason: outlierResult.reason ?? null,
  };

  let newAvg = state.avgFeedingsPerRefill;
  if (!outlierResult.isOutlier) {
    newAvg = updateEMA(state.avgFeedingsPerRefill, closedCycle.feedingsCount);
  }

  const newCyclesCompleted = state.cyclesCompleted + 1;
  const newLearningStatus =
    newCyclesCompleted >= LEARNING.MIN_CYCLES_TO_CALIBRATE
      ? 'calibrated'
      : 'learning';

  // Rolling window: si el array excede el cap, descartamos el más viejo.
  const allCycles = [...state.cycles, cycleWithOutlierFlag];
  const trimmedCycles =
    allCycles.length > LEARNING.CYCLES_CAP
      ? allCycles.slice(allCycles.length - LEARNING.CYCLES_CAP)
      : allCycles;

  return {
    cycles: trimmedCycles,
    closedCycle: cycleWithOutlierFlag,
    avgFeedingsPerRefill: newAvg,
    avgRefillsPerBag: state.avgRefillsPerBag,
    cyclesCompleted: newCyclesCompleted,
    learningStatus: newLearningStatus,
  };
};

// =====================================================================
// ESTIMACIONES DE AUTONOMÍA
// =====================================================================

/**
 * Estima cuántas comidas quedan en el dispenser.
 *
 * Hay dos formas de estimar:
 *  - Estricta (contable): dispenserStockGr / portionGr
 *  - Aprendida: usa avgFeedingsPerRefill, ajustado proporcionalmente al
 *    nivel actual vs el refillAmount promedio.
 *
 * Devolvemos ambas porque tienen usos distintos:
 *  - Estricta para alertas de "stock insuficiente para la próxima ración"
 *  - Aprendida para "cuántas comidas reales quedan según tus hábitos"
 *
 * Cuando aún no hay calibración, "aprendida" cae al valor estricto.
 */
export const estimateRemainingFeedings = ({
  dispenserStockGr,
  portionGr,
  avgFeedingsPerRefill,
  cycles,
  learningStatus,
}) => {
  const strict = portionGr > 0 ? Math.floor(dispenserStockGr / portionGr) : 0;

  if (learningStatus !== 'calibrated' || !avgFeedingsPerRefill || avgFeedingsPerRefill <= 0) {
    return { strict, learned: strict, confidence: 'low' };
  }

  // El refillAmount promedio (de ciclos válidos) nos dice cuánto "vale" un
  // dispenser-lleno medido en gramos. La proporción del stock actual sobre
  // ese promedio nos da el ratio.
  const validCycles = cycles.filter((c) => !c.isOutlier && c.endedAt && c.refillAmountGr > 0);
  if (validCycles.length === 0) {
    return { strict, learned: strict, confidence: 'low' };
  }
  const avgRefillAmount =
    validCycles.reduce((s, c) => s + c.refillAmountGr, 0) / validCycles.length;

  if (avgRefillAmount <= 0) {
    return { strict, learned: strict, confidence: 'low' };
  }

  const ratio = dispenserStockGr / avgRefillAmount;
  const learned = Math.floor(avgFeedingsPerRefill * ratio);

  return {
    strict,
    learned: Math.max(0, learned),
    confidence: validCycles.length >= 5 ? 'high' : 'medium',
  };
};

/**
 * Estima cuántos días autónomos quedan.
 * Trivial sobre estimateRemainingFeedings: días = feedings / feedingsPerDay.
 */
export const estimateDaysRemaining = ({
  remainingFeedings,
  feedingsPerDay,
}) => {
  if (!feedingsPerDay || feedingsPerDay <= 0) return null;
  return remainingFeedings / feedingsPerDay;
};

// =====================================================================
// PROYECCIÓN A NIVEL GRUPO (Fase 6)
// =====================================================================

/**
 * Estima cuántos días de autonomía le quedan a un GRUPO de alimentación
 * considerando el stock TOTAL (bolsa principal + dispensers + bolsas en
 * reserva) y la suma del consumo diario de TODAS las mascotas que comparten
 * el grupo.
 *
 * Diferencias con `getPetEstimations`:
 *  - Esa función estima por mascota individual y solo mira el stock
 *    "alimentable inmediato" (dispenser o main según el modo). NO incluye
 *    las bolsas en reserva.
 *  - `estimateGroupProjection` mira el horizonte completo: cuánto stock
 *    hay sumando todas las capas (todo lo que el hogar tiene comprado de
 *    esta comida), dividido por el consumo agregado del grupo.
 *
 * Esto es lo que sirve para decidir "tengo que comprar pronto".
 *
 * @param {object} group - feedingGroup con dispensers, mainStockGr,
 *   bagCapacityGr, extraBagsCount, lowStockThresholdDays.
 * @param {Array<object>} pets - mascotas activas (no soft-deleted) que
 *   pertenecen a este grupo (pet.feedingGroupId === group.id).
 * @param {object} [opts]
 * @param {number} [opts.defaultThresholdDays] - umbral default si el
 *   grupo no tiene `lowStockThresholdDays` propio.
 * @returns {{
 *   totalStockGr: number,
 *   dispenserStockGr: number,
 *   mainStockGr: number,
 *   reserveStockGr: number,
 *   dailyConsumptionGr: number,
 *   daysRemaining: number | null,    // null si dailyConsumption === 0
 *   thresholdDays: number,
 *   isLowStock: boolean,             // true si daysRemaining < thresholdDays
 *   isOutOfStock: boolean,           // true si totalStock === 0
 *   hasActivePets: boolean,          // false si pets.length === 0 (sin consumo)
 * } | null}
 */
export const estimateGroupProjection = (group, pets, opts = {}) => {
  if (!group) return null;

  const defaultThreshold = opts.defaultThresholdDays ?? 3;

  const dispensers = group.dispensers ?? [];
  const dispenserStockGr = dispensers.reduce(
    (acc, d) => acc + (d.stockGr ?? 0),
    0
  );
  const mainStockGr = group.mainStockGr ?? 0;
  const bagCapacityGr = group.bagCapacityGr ?? 0;
  const extraBagsCount = group.extraBagsCount ?? 0;
  const reserveStockGr = bagCapacityGr * extraBagsCount;

  const totalStockGr = dispenserStockGr + mainStockGr + reserveStockGr;

  const activePets = (pets ?? []).filter((p) => !p.deletedAt);
  const dailyConsumptionGr = activePets.reduce(
    (acc, p) => acc + (p.dailyRationGr ?? 0),
    0
  );

  const thresholdDays = group.lowStockThresholdDays ?? defaultThreshold;

  // Sin pets activas el grupo no consume — daysRemaining no aplica.
  // Lo dejamos null para que la UI muestre "Sin mascotas asignadas" en vez
  // de "X días restantes" (que sería engañoso).
  if (activePets.length === 0 || dailyConsumptionGr === 0) {
    return {
      totalStockGr,
      dispenserStockGr,
      mainStockGr,
      reserveStockGr,
      dailyConsumptionGr: 0,
      daysRemaining: null,
      thresholdDays,
      isLowStock: false,
      isOutOfStock: totalStockGr === 0,
      hasActivePets: false,
    };
  }

  const daysRemaining = totalStockGr / dailyConsumptionGr;
  const isLowStock = daysRemaining < thresholdDays;

  return {
    totalStockGr,
    dispenserStockGr,
    mainStockGr,
    reserveStockGr,
    dailyConsumptionGr,
    daysRemaining,
    thresholdDays,
    isLowStock,
    isOutOfStock: totalStockGr === 0,
    hasActivePets: true,
  };
};